import React, { useState, useEffect } from 'react';
import { Modal, Button } from 'react-bootstrap';
import Cropper from 'react-easy-crop';
import { uploadData, getUrl } from 'aws-amplify/storage';
import { fetchUserProfileFromDB, saveUserProfileToDB } from './dynamoUtils';
import { DEFAULT_AVATAR as defaultAvatar } from "./avatar";
import { AiFillEdit } from 'react-icons/ai';

const AvatarEditor = ({ username, initialAvatarUrl = defaultAvatar, onAvatarChange = () => { } }) => {
  const [avatarUrl, setAvatarUrl] = useState(initialAvatarUrl);
  const [originalImage, setOriginalImage] = useState(null);
  const [showEditOptions, setShowEditOptions] = useState(false);
  const [showCropper, setShowCropper] = useState(false);
  const [cropImage, setCropImage] = useState(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [saveStatus, setSaveStatus] = useState('idle'); // 'idle' | 'saving' | 'saved' | 'error'


  useEffect(() => {
    const fetchAvatar = async () => {
      const profile = await fetchUserProfileFromDB(username);
      const avatar = profile?.avatar || defaultAvatar;
      const avatarOriginal = profile?.avatarOriginal || 'none'; // ✅ fallback here

      const safeAvatar =
        !avatar || avatar.trim().toLowerCase() === 'none'
          ? defaultAvatar
          : avatar;

      setAvatarUrl(safeAvatar);
      setOriginalImage(avatarOriginal !== 'none' ? avatarOriginal : null);
    };
    fetchAvatar();
  }, [username]);

  useEffect(() => {
    const fetchAvatar = async () => {
      const profile = await fetchUserProfileFromDB(username);
      const avatar = profile?.avatar || defaultAvatar;
      const avatarOriginal = profile?.avatarOriginal || null;

      const safeAvatar =
        !avatar || avatar.trim().toLowerCase() === 'none'
          ? defaultAvatar
          : avatar;

      setAvatarUrl(safeAvatar);

      if (avatarOriginal && avatarOriginal !== 'none') {
        setOriginalImage(avatarOriginal); // ✅ Save actual S3 link instead of blob
      }
    };
    fetchAvatar();
  }, [username]);

  const DEFAULT_AVATAR_NAME = "avatar_alien_white.jpeg";

  const isDefaultAvatarUrl = (url) => {
    if (!url) return true;
    const base = url.split("?")[0].toLowerCase();
    return base.endsWith(`/${DEFAULT_AVATAR_NAME}`) || base === "none";
  };

  const isUneditableAvatar = isDefaultAvatarUrl(avatarUrl) || !originalImage;


  const handleRemoveAvatar = async () => {
    setAvatarUrl(defaultAvatar);
    await saveUserProfileToDB(username, {
      avatar: defaultAvatar,
      avatarOriginal: 'none'
    });
    setOriginalImage(null);
    setShowEditOptions(false);
    onAvatarChange(defaultAvatar);
  };

  const handleEditAvatar = async () => {
    try {
      const profile = await fetchUserProfileFromDB(username);
      const avatarOriginal = profile?.avatarOriginal;

      if (avatarOriginal && avatarOriginal !== 'none') {
        setOriginalImage(avatarOriginal);
        setCropImage(avatarOriginal);
      } else {
        console.warn('⚠️ No original avatar found, using current avatar.');
        setCropImage(avatarUrl);
      }

      setZoom(1);
      setShowEditOptions(true);
    } catch (err) {
      console.error('❌ Failed to fetch original image for cropping:', err);
      setCropImage(avatarUrl);
      setShowEditOptions(true);
    }

    setZoom(1);
    setShowEditOptions(true);
  };

  const handleCropSave = async () => {
    try {
      setSaveStatus('saving'); // 👈 user feedback: grey "Saving..."

      const croppedImageURL = await getCroppedImg();
      const blob = await compressImage(croppedImageURL);
      const file = new File([blob], `avatar_${username}.jpg`, { type: 'image/jpeg' });

      const key = `avatars/avatar_${username}.jpg`;

      await uploadData({
        key,
        data: file,
        options: { accessLevel: 'public', contentType: 'image/jpeg' }
      }).result;

      const { url } = await getUrl({ key, options: { accessLevel: 'public' } });
      const finalUrl = url.toString();

      await saveUserProfileToDB(username, {
        avatar: finalUrl,
        avatarOriginal: originalImage
      });

      setAvatarUrl(finalUrl);
      onAvatarChange(finalUrl);

      setSaveStatus('saved'); // 👈 turn button green

      setShowCropper(false);
      // optional: reset back to idle after a bit
      setTimeout(() => setSaveStatus('idle'), 500);
    } catch (err) {
      console.error('Failed to save avatar:', err);
      setSaveStatus('error'); // 👈 could show red "Error" if you want
    }
  };


  const getCroppedImg = async () => {
    const image = new Image();
    image.src = cropImage;
    await new Promise(resolve => (image.onload = resolve));

    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');

    const scaleX = image.naturalWidth / image.width;
    const scaleY = image.naturalHeight / image.height;

    canvas.width = croppedAreaPixels.width;
    canvas.height = croppedAreaPixels.height;

    ctx.drawImage(
      image,
      croppedAreaPixels.x * scaleX,
      croppedAreaPixels.y * scaleY,
      croppedAreaPixels.width * scaleX,
      croppedAreaPixels.height * scaleY,
      0,
      0,
      croppedAreaPixels.width,
      croppedAreaPixels.height
    );

    return new Promise(resolve => {
      canvas.toBlob(blob => {
        if (blob) {
          resolve(URL.createObjectURL(blob));
        }
      }, 'image/jpeg');
    });
  };

  const compressImage = async imageUrl => {
    return new Promise(resolve => {
      const img = new Image();
      img.src = imageUrl;
      img.onload = () => {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');

        const MAX_SIZE = 300;
        let width = img.width;
        let height = img.height;

        if (width > height && width > MAX_SIZE) {
          height *= MAX_SIZE / width;
          width = MAX_SIZE;
        } else if (height > MAX_SIZE) {
          width *= MAX_SIZE / height;
          height = MAX_SIZE;
        }

        canvas.width = width;
        canvas.height = height;
        ctx.drawImage(img, 0, 0, width, height);

        canvas.toBlob(blob => {
          resolve(blob);
        }, 'image/jpeg', 0.7);
      };
    });
  };

  const handleNewAvatarUpload = async e => {
    const file = e.target.files[0];
    if (!file) return;

    const fileURL = URL.createObjectURL(file);
    setCropImage(fileURL);
    setShowCropper(true);
    setShowEditOptions(false);

    const originalKey = `avatars/original_avatar_${username}.jpg`;

    // v6 upload
    await uploadData({
      key: originalKey,
      data: file,
      options: { accessLevel: 'public', contentType: 'image/jpeg' }
    }).result;

    // v6 public URL
    const { url: originalUrlObj } = await getUrl({
      key: originalKey,
      options: { accessLevel: 'public' }
    });
    const originalLink = originalUrlObj.toString();  // stable URL

    setOriginalImage(originalLink);
    setAvatarUrl(originalLink); // preview

    const profile = await fetchUserProfileFromDB(username);
    if (!profile?.avatarOriginal || profile.avatarOriginal === 'none') {
      await saveUserProfileToDB(username, { avatarOriginal: originalLink });
    }

  };

  const fetchAndConvertToLocalURL = async imageUrl => {
    const response = await fetch(imageUrl, { mode: 'cors' });
    const blob = await response.blob();
    return URL.createObjectURL(blob);
  };

  return (
    <>
      <label className="settings-avatar-label" onClick={handleEditAvatar}>
        <img src={avatarUrl} alt="Avatar" className="settings-avatar-image" />
        <div className="settings-avatar-overlay">
          <AiFillEdit className="settings-avatar-edit-icon" />
        </div>
      </label>

      <Modal
        show={showEditOptions}
        onHide={() => setShowEditOptions(false)}
        centered
        backdropClassName="avatar-editor-backdrop"
        dialogClassName="avatar-editor-modal"
      >
        <Modal.Header closeButton closeVariant="white">
          <Modal.Title>Edit Avatar</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Button variant="danger" onClick={handleRemoveAvatar} style={{ marginRight: '10px' }}>
            Remove
          </Button>
          <Button
            variant="primary"
            onClick={() => document.getElementById('avatar-upload').click()}
            style={{ marginRight: '10px' }}
          >
            New
          </Button>
          <Button
            variant="info"
            className="settings-avatar-edit-button"
            onClick={async () => {
              if (originalImage && originalImage !== 'none') {
                const safeURL = await fetchAndConvertToLocalURL(originalImage);
                setCropImage(safeURL);
              } else {
                const safeURL = await fetchAndConvertToLocalURL(avatarUrl);
                setCropImage(safeURL);
              }
              setShowEditOptions(false);
              setShowCropper(true);
            }}
            disabled={isUneditableAvatar}
            style={{
              opacity: isUneditableAvatar ? 0.3 : 1,
              cursor: isUneditableAvatar ? 'not-allowed' : 'pointer',
              filter: isUneditableAvatar ? 'grayscale(100%)' : 'none',
              pointerEvents: isUneditableAvatar ? 'none' : 'auto'
            }}
          >
            Edit
          </Button>
          <input id="avatar-upload" type="file" accept="image/*" style={{ display: 'none' }} onChange={handleNewAvatarUpload} />
        </Modal.Body>
      </Modal>

      <Modal
        show={showCropper}
        onHide={() => setShowCropper(false)}
        centered
        backdropClassName="avatar-editor-backdrop"
        dialogClassName="avatar-editor-modal"
      >
        <Modal.Header closeButton closeVariant="white">
          <Modal.Title>Crop your image</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {cropImage && (
            <div style={{ position: 'relative', width: '100%', height: 300 }}>
              <Cropper
                image={cropImage}
                crop={crop}
                zoom={zoom}
                aspect={1}
                onCropChange={setCrop}
                onZoomChange={setZoom}
                onCropComplete={(_, croppedPixels) => setCroppedAreaPixels(croppedPixels)}
              />
            </div>
          )}
          <div className="settings-controls">
            <input
              type="range"
              min="1"
              max="3"
              step="0.1"
              value={zoom}
              onChange={e => setZoom(Number(e.target.value))}
              className="settings-slider"
            />
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowCropper(false)}>
            Cancel
          </Button>
          <Button
            variant={
              saveStatus === 'saving'
                ? 'secondary' // grey
                : saveStatus === 'saved'
                  ? 'success'   // green
                  : saveStatus === 'error'
                    ? 'danger'    // red (optional)
                    : 'primary'
            }
            onClick={handleCropSave}
            disabled={saveStatus === 'saving'} // can't double-click while saving
          >
            {saveStatus === 'saving'
              ? 'Saving...'
              : saveStatus === 'saved'
                ? 'Saved'
                : saveStatus === 'error'
                  ? 'Error'
                  : 'Save'}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default AvatarEditor;
